import React from "react";
import { Box, Sheet, Stack } from "@mui/joy";
import CommonModal from "../../common/CommonModal";
import DarkInput from "../../common/DarkInput";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import Pagination from "../../common/Pagination";
import { SCreate } from "../../../styles/StyledMUI";
import { colorTokens, textTokens } from "../../../styles/globalStyles";
import { useEquipment } from "../../../contexts/api";
import { useRental } from "../../../contexts/api";
import { useAlert } from "../../../contexts/app/notificationToast";
import type { Equipment, EquipmentModel } from "../../../api/equipment/Equipment.typed";
import { equipmentLabel, formatAttributeValue } from "./rentalItemHelpers";

const PAGE_SIZE = 10;

type Props = {
  open: boolean;
  modelID: number | null;
  modelName: string;
  onClose: () => void;
  onPicked: (equipmentID: string) => Promise<boolean>;
};

export default function RentalEquipmentPickerModal({
  open,
  modelID,
  modelName,
  onClose,
  onPicked,
}: Props) {
  const { listAvailableEquipments } = useRental();
  const { getEquipmentModel } = useEquipment();
  const { showAlert } = useAlert();

  const [equipments, setEquipments] = React.useState<Equipment[]>([]);
  const [model, setModel] = React.useState<EquipmentModel | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(PAGE_SIZE);
  const [addingID, setAddingID] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setQuery("");
    setCurrentPage(1);
  }, [open, modelID]);

  const refresh = React.useCallback(
    async (q: string) => {
      if (modelID == null) {
        setEquipments([]);
        return;
      }

      setLoading(true);
      const result = await listAvailableEquipments(modelID, q.trim() || undefined);
      setEquipments(result ?? []);
      setCurrentPage(1);
      setLoading(false);
    },
    [modelID, listAvailableEquipments],
  );

  React.useEffect(() => {
    if (!open) return;
    refresh(query);
  }, [open, refresh]);

  // Debounce text search so we don't fire on every keystroke.
  React.useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => refresh(query), 250);
    return () => window.clearTimeout(handle);
  }, [open, query, refresh]);

  // Fetch model attribute definitions for rich display; gracefully skip if unavailable.
  React.useEffect(() => {
    if (!open || modelID == null) return;
    let cancelled = false;
    (async () => {
      const fetched = await getEquipmentModel(modelID);
      if (!cancelled) setModel(fetched ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, modelID, getEquipmentModel]);

  const totalItems = equipments.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageEquipments = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return equipments.slice(start, start + itemsPerPage);
  }, [equipments, currentPage, itemsPerPage]);

  const handleAdd = async (eq: Equipment) => {
    if (!eq.ID) return;
    const id = eq.ID;
    setAddingID(id);
    const ok = await onPicked(id);
    setAddingID(null);
    if (!ok) {
      showAlert("Impossible d'ajouter l'équipement", "danger");
      return;
    }

    setEquipments((prev) => prev.filter((e) => e.ID !== id));
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Choisir un équipement"
      subtitle={modelName}
      size="xl"
    >
      <Stack spacing={1.5}>
        <DarkInput
          placeholder="Rechercher par nom, référence, UUID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Rechercher un équipement"
        />

        {loading && (
          <DarkTypography level="body-sm" sx={{ color: textTokens.color.dimmed }}>
            Chargement…
          </DarkTypography>
        )}

        {!loading && totalItems === 0 && (
          <DarkTypography level="body-sm" sx={{ color: textTokens.color.dimmed }}>
            Aucun équipement disponible pour ce modèle.
          </DarkTypography>
        )}

        {!loading && totalItems > 0 && (
          <Stack spacing={0.5}>
            {pageEquipments.map((eq) => {
              const attrs = (model?.Attributes ?? []).filter(
                (a) => formatAttributeValue(a, eq.Attributes?.[a.ID]) !== "",
              );

              return (
                <Sheet
                  key={eq.ID ?? `${eq.ModelID}-${equipmentLabel(eq)}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: "sm",
                    backgroundColor: colorTokens.bg.surface,
                    border: `1px solid ${colorTokens.border.default}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <DarkTypography
                      level="body-sm"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {equipmentLabel(eq)}
                    </DarkTypography>
                    {eq.ID && (
                      <DarkTypography
                        level="body-xs"
                        colorVariant="muted"
                        sx={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {eq.ID.slice(0, 8)}
                      </DarkTypography>
                    )}
                    {attrs.length > 0 && (
                      <Box
                        sx={{
                          mt: 0.5,
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                          },
                          columnGap: 1,
                          rowGap: 0.25,
                        }}
                      >
                        {attrs.map((a) => (
                          <Box key={a.ID} sx={{ minWidth: 0 }}>
                            <DarkTypography
                              level="body-xs"
                              colorVariant="muted"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                fontSize: "0.6rem",
                              }}
                            >
                              {a.Label}
                            </DarkTypography>
                            <DarkTypography
                              level="body-xs"
                              sx={{
                                color: textTokens.color.primary,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {formatAttributeValue(a, eq.Attributes?.[a.ID])}
                            </DarkTypography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <IconButtonCTA
                    aria-label="Ajouter au panier"
                    size="sm"
                    ctaVariant="success"
                    disabled={addingID != null}
                    onClick={() => handleAdd(eq)}
                  >
                    <SCreate />
                  </IconButtonCTA>
                </Sheet>
              );
            })}
          </Stack>
        )}

        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(limit) => {
              setItemsPerPage(limit);
              setCurrentPage(1);
            }}
            loading={loading}
          />
        )}
      </Stack>
    </CommonModal>
  );
}
